import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useCurrentBusiness } from "./use-current-business";
import { useToast } from "./use-toast";

export interface Invoice {
  id: number;
  businessId: number;
  customerId: number;
  invoiceNumber: string;
  date: string;
  issueDate: string;
  dueDate: string;
  due_date?: string;
  status: "draft" | "sent" | "paid" | "overdue" | "cancelled";
  total: number;
  subtotal: number;
  taxAmount: number;
  notes: string | null;
  terms: string | null;
  customerName?: string;
  items?: InvoiceItem[];
}

export interface InvoiceItem {
  id: number;
  invoiceId: number;
  productId: number | null;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  taxRate: number;
  productName?: string;
}

export interface CreateInvoiceData {
  customerId: number;
  issueDate: string;
  dueDate: string;
  status: "draft" | "sent" | "paid" | "overdue" | "cancelled";
  notes?: string;
  terms?: string;
  items: Array<{
    productId?: number;
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate?: number;
  }>;
}

export function useInvoices() {
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  
  // Fetch all invoices for the current business
  const {
    data: invoices,
    isLoading,
    error,
    refetch
  } = useQuery<Invoice[], Error>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/invoices`],
    queryFn: async () => {
      if (!currentBusiness?.id) return [];
      const res = await apiRequest("GET", `/api/businesses/${currentBusiness.id}/invoices`);
      if (!res.ok) {
        throw new Error("Failed to fetch invoices");
      }
      return res.json() as Promise<Invoice[]>;
    },
    enabled: !!currentBusiness?.id,
  });

  // Fetch a single invoice by ID
  const useInvoice = (invoiceId: number) => {
    return useQuery<Invoice | null, Error>({
      queryKey: [`/api/businesses/${currentBusiness?.id}/invoices/${invoiceId}`],
      queryFn: async () => {
        if (!currentBusiness?.id || !invoiceId) return null;
        const res = await apiRequest("GET", `/api/businesses/${currentBusiness.id}/invoices/${invoiceId}`);
        if (!res.ok) {
          throw new Error("Failed to fetch invoice");
        }
        return res.json() as Promise<Invoice>;
      },
      enabled: !!currentBusiness?.id && !!invoiceId,
    });
  };

  // Create a new invoice
  const createInvoiceMutation = useMutation({
    mutationFn: async (data: CreateInvoiceData) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      const res = await apiRequest("POST", `/api/businesses/${currentBusiness.id}/invoices`, data);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to create invoice");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Invoice created successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an existing invoice
  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateInvoiceData> }) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      const res = await apiRequest("PATCH", `/api/businesses/${currentBusiness.id}/invoices/${id}`, data);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update invoice");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: "Invoice updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices`] });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices/${data.id}`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete an invoice
  const deleteInvoiceMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      const res = await apiRequest("DELETE", `/api/businesses/${currentBusiness.id}/invoices/${id}`);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete invoice");
      }
      return id;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Invoice deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // Dedicated mutation for updating invoice status
  const updateInvoiceStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' }) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      console.log(`Updating invoice ${id} status to ${status}`);
      const res = await apiRequest(
        "PATCH", 
        `/api/businesses/${currentBusiness.id}/invoices/${id}/status`, 
        { status }
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update invoice status");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: `Invoice status updated to ${data.status}`,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices`] });
      if (data.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices/${data.id}`] });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    invoices,
    isLoading,
    error,
    refetch,
    useInvoice,
    createInvoiceMutation,
    updateInvoiceMutation,
    updateInvoiceStatusMutation,
    deleteInvoiceMutation,
  };
}
